'use client';

/**
 * ConvictionGauge — the signature conviction meter.
 *
 * Enhanced-aware drop-in for the terminal `ConvictionMeter` (same prop shape:
 * `value`, `call`, `showValue`, `className`). It cascades automatically to every
 * consumer (recs list, signal panel, Why-adjacent surfaces) with no page edits.
 *
 *  - Enhanced OFF → renders EXACTLY the current flat bar + "{n} / 10". The output
 *    is byte-identical to `ConvictionMeter`, so the baseline never shifts.
 *  - Enhanced ON  → renders a 10-segment notched gauge: ten small segments, the
 *    first `score` filled in the call color, each carrying `sig-gauge-seg` with a
 *    staggered fill (per-segment transition-delay), and the score rendered as the
 *    Instrument Serif numeral (`font-serif`) beside it.
 *
 * Score is binned into bands for the label/tint nuance — Watch (1-3),
 * Constructive (4-6), High (7-8), Top (9-10) — while keeping the call-color
 * semantics (buy/hold/sell token) for the fill itself.
 */

import { useEnhancedUi } from '@/lib/ui/use-enhanced-ui';
import { cn } from '@/lib/utils';

export type ConvictionCall = 'BUY' | 'SELL' | 'HOLD';

/** call → theme token (buy/hold/sell). Mirrors the terminal kit's CALL_VAR. */
const CALL_VAR: Record<ConvictionCall, string> = {
  BUY: 'var(--color-buy)',
  SELL: 'var(--color-sell)',
  HOLD: 'var(--color-hold)',
};

export type ConvictionBand = 'watch' | 'constructive' | 'high' | 'top';

/** Bin a 1-10 score into a conviction band (label/tint nuance only). */
export function convictionBand(score: number): ConvictionBand {
  if (score <= 3) return 'watch';
  if (score <= 6) return 'constructive';
  if (score <= 8) return 'high';
  return 'top';
}

const BAND_WORD: Record<ConvictionBand, string> = {
  watch: 'watch',
  constructive: 'constructive',
  high: 'high',
  top: 'top',
};

const SEGMENT_COUNT = 10;

/**
 * The notched gauge body, shared so other surfaces (e.g. ConvictionBadge) can
 * render the same segments. Renders 10 segments; the first `filled` are tinted
 * in `color`, each with a staggered `sig-gauge-seg` fill.
 */
export function GaugeSegments({
  filled,
  color,
  className,
}: {
  filled: number;
  color: string;
  className?: string;
}) {
  const clamped = Math.max(0, Math.min(SEGMENT_COUNT, Math.round(filled)));
  return (
    <div className={cn('flex items-center gap-[2px]', className)} aria-hidden="true">
      {Array.from({ length: SEGMENT_COUNT }, (_, i) => {
        const on = i < clamped;
        return (
          <span
            key={i}
            className="sig-gauge-seg h-[10px] w-[5px] rounded-[1px]"
            style={{
              background: on ? color : 'var(--color-hair-2)',
              boxShadow: on ? `0 0 4px color-mix(in srgb, ${color} 45%, transparent)` : 'none',
              transitionDelay: `${i * 28}ms`,
            }}
          />
        );
      })}
    </div>
  );
}

export interface ConvictionGaugeProps {
  /** Conviction 0–10. */
  value: number;
  /** Call color the fill takes. Defaults to BUY (matches ConvictionMeter). */
  call?: ConvictionCall;
  /** Show the numeric readout beside the meter. */
  showValue?: boolean;
  className?: string;
}

/**
 * Drop-in conviction meter. Enhanced-aware: notched gauge when on, the original
 * flat bar when off (byte-identical to `ConvictionMeter`).
 */
export function ConvictionGauge({
  value,
  call = 'BUY',
  showValue = true,
  className,
}: ConvictionGaugeProps) {
  const enhanced = useEnhancedUi();
  const color = CALL_VAR[call];

  if (!enhanced) {
    // BASELINE — kept byte-identical to ConvictionMeter (terminal kit).
    const pct = Math.max(0, Math.min(10, value)) * 10;
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <div className="h-1 w-[120px] overflow-hidden rounded-full bg-[var(--color-hair-2)]">
          <span
            className="block h-full rounded-full"
            style={{ width: `${pct}%`, background: color }}
          />
        </div>
        {showValue && (
          <span className="font-mono text-[12px] tabular-nums text-[var(--color-text-muted)]">
            {value.toFixed(1)} / 10
          </span>
        )}
      </div>
    );
  }

  // ENHANCED — 10-segment notched gauge + serif numeral.
  const rounded = Math.max(0, Math.min(10, Math.round(value)));
  const band = convictionBand(rounded);
  return (
    <div
      className={cn('flex items-center gap-2', className)}
      role="img"
      aria-label={`conviction ${rounded} of 10, ${BAND_WORD[band]}`}
    >
      {/* Call color stays on the graphical gauge segments (3:1 is enough for
          graphics). The serif numeral readout uses --color-text so it clears
          WCAG AA 4.5:1 in both themes. */}
      <GaugeSegments filled={rounded} color={color} />
      {showValue && (
        <span className="font-serif text-[18px] leading-none tabular-nums text-[var(--color-text)]">
          {rounded}
        </span>
      )}
    </div>
  );
}
